import { motion } from 'framer-motion';
import { DollarSign, Package, Headphones, Zap, ShieldCheck, Award } from 'lucide-react';

export const Features = () => {
  const features = [
    {
      icon: DollarSign,
      title: 'Competitive Pricing',
      description: 'We offer unmatched value through wholesale pricing on premium technology products, tailored to your business requirements and order volumes.',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      icon: Package,
      title: 'Extensive Inventory',
      description: 'Access a broad range of technology hardware, from individual components through to complete computing systems, sourced through our international network to match what your customers demand.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Headphones,
      title: 'Exceptional Support',
      description: 'Our dedicated team is available around the clock to assist with enquiries, order tracking, and after-sales support for a seamless experience.',
      color: 'from-violet-500 to-purple-500'
    },
    {
      icon: Zap,
      title: 'Cutting-Edge Technology',
      description: 'Stay ahead of the market with current-generation GPUs, processors, memory, storage and complete systems, available for wholesale as they reach the channel.',
      color: 'from-orange-500 to-amber-500'
    },
    {
      icon: ShieldCheck,
      title: 'Fully Insured Transactions',
      description: 'Every transaction is protected through our comprehensive insurance coverage with AIG and Atradius, providing complete peace of mind.',
      color: 'from-sky-500 to-blue-600'
    },
    {
      icon: Award,
      title: 'Authorised Distributors',
      description: 'We work exclusively with authorised distributors worldwide, guaranteeing brand-new, sealed products in their original retail packaging.',
      color: 'from-cyan-500 to-blue-500'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  return (
    <section className="section-padding bg-slate-50 relative overflow-hidden" data-testid="features-section">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#0096D6]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-slate-200/50 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-[#0096D6]/10 text-[#0096D6] font-manrope text-sm font-medium mb-4">
            Why Choose Us
          </span>
          <h2 className="font-outfit text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Key Benefits
          </h2>
          <p className="font-manrope text-slate-600 text-lg max-w-2xl mx-auto">
            We deliver exceptional value through quality products, competitive pricing, and world-class service.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="opulent-card p-8 group"
              data-testid={`feature-card-${index}`}
            >
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500`}>
                <feature.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">
                {feature.title}
              </h3>
              <p className="font-manrope text-slate-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
